import React, {useEffect, useRef} from 'react';
import {Linking} from 'react-native';
import {
  DefaultTheme,
  getStateFromPath,
  InitialState,
  LinkingOptions,
  NavigationContainer,
} from '@react-navigation/native';

import config from '../../app/config';
import * as RootNavigation from '../../app/navigation/RootNavigation';
import {useStores} from '../../app/stores/use-stores';
import {fontFamily} from '../components/ui-controls/Text';
import {
  NAVIGATION_STATE_KEY,
  NAVIGATION_TIMESTAMP_KEY,
  NAVIGATION_URL_KEY,
} from '../constants/sessionStorageKeys';
import {AppRoute} from '../models/AppRoute';
import {navStructure} from '../navigation/linking';
import {
  ALL_SEGMENT_VALUE,
  feedDiscoverListQueryFn,
  feedDiscoverListQueryKey,
} from '../queries/feed/useFeedDiscoverList';
import {queryClient} from '../queries/queryClient';
import {crashlyticsService} from '../services/CrashlyticsService';
import {rootStore} from '../stores/rootStore';
import {logger} from '../utils/logger';
import {clearLastNotificationUrl, getLastNotificationUrl} from '../utils/onesignal';

const theme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: 'white',
  },
  fonts: {
    regular: {fontFamily, fontWeight: '400' as const},
    medium: {fontFamily, fontWeight: '500' as const},
    bold: {fontFamily, fontWeight: '600' as const},
    heavy: {fontFamily, fontWeight: '700' as const},
  },
};

// We need to persist state, otherwise the back buttons don't work in the case
// of a refresh. See https://reactnavigation.org/docs/state-persistence/ and
// https://liquormarketinggroup.atlassian.net/browse/LLP-392

// After 10 minutes, we want to ignore this and allow hard refreshes.
// A scenario we want to preserve is that a user can manually change the URL
// in the address bar, and they don't get redirected or have weird navigation
// stack state loaded in because of this solution
const STATE_EXPIRATION_TIME = 10 * 60 * 1000; // 10 minutes

const getCurrentRouteName = () => RootNavigation?.navigationRef?.getCurrentRoute()?.name;

export default function AppNavigationContainer({
  children,
  initialState,
}: {
  children: React.ReactNode;
  initialState?: InitialState;
}) {
  const {globalStore} = useStores();
  const [isReady, setIsReady] = React.useState(false);
  const [initialNavigationState, setInitialNavigationState] = React.useState<
    InitialState | undefined
  >(initialState);

  useEffect(() => {
    const _restoreState = async () => {
      try {
        // sessionStorage is per tab, rather than localStorage, which is per domain
        // this is safest behaviour for state restoration via opening new links
        const savedStateString = sessionStorage.getItem(NAVIGATION_STATE_KEY);
        const savedTimestamp = sessionStorage.getItem(NAVIGATION_TIMESTAMP_KEY);
        const savedUrl = sessionStorage.getItem(NAVIGATION_URL_KEY);

        const currentTimestamp = Date.now();
        const currentUrl = window.location.href;

        if (savedStateString && savedTimestamp && savedUrl) {
          const state = JSON.parse(savedStateString);
          const timestamp = parseInt(savedTimestamp, 10);

          const isExpired = currentTimestamp - timestamp > STATE_EXPIRATION_TIME;
          const isSameUrl = currentUrl === savedUrl;

          if (!isExpired && isSameUrl) {
            setInitialNavigationState(state);
          } else {
            sessionStorage.removeItem(NAVIGATION_STATE_KEY);
            sessionStorage.removeItem(NAVIGATION_TIMESTAMP_KEY);
            sessionStorage.removeItem(NAVIGATION_URL_KEY);
          }
        }
      } catch (_error) {
        // If an error occurs during state restoration, clear the persisted state
        // So the user don't get stuck in a loop of errors and refresh doesn't recify the issue
        sessionStorage.removeItem(NAVIGATION_STATE_KEY);
        sessionStorage.removeItem(NAVIGATION_TIMESTAMP_KEY);
        sessionStorage.removeItem(NAVIGATION_URL_KEY);
      } finally {
        setIsReady(true);
      }
    };

    // if (!initialState && Platform.OS === 'web') {
    // TODO: at the moment this solution causes a severe impact bug, where the user
    // tries to change their store, and after the store is selected, instead of navigating
    // home, it will navigate back to the store selection page. I.e it gets stuck
    // on /MY_STORE_STORE_SELECTION
    // restoreState();
    // } else {
    setIsReady(true);
    // }
  }, [initialState]);

  const linking: LinkingOptions<object> = {
    // Prefixes for Mobile
    prefixes: [
      `${config.WHITE_LABEL_ID}://`,
      `https://${config.HOST_DOMAIN}`,
      `https://beta.${config.HOST_DOMAIN}`,
      `https://${config.WHITE_LABEL_ID}-brand-dev.lmg-bw.au`, // test domain
    ],
    config: navStructure,
    getStateFromPath: (path: string, options?: object) => {
      const originalNavigationState = getStateFromPath(path, options);
      globalStore.setDelayedNavigationPath(path);
      // DIS-1306: warm-start prefetch — kick off the ALL-segment first page on
      // cold deep links to /discover/<slug> so FeedDiscoverDetailScreen's
      // stub-resolve loop short-circuits when discoverList populates. Shares
      // the infinite-query key with `useFeedDiscoverList` (segment 'ALL') so
      // DiscoverListScreen's mount coalesces with this prefetch.
      if (/^\/discover\/[^/]+$/.test(path)) {
        const selectedStore = rootStore?.shopSelectionStore?.selectedStore;
        const storeNumber = selectedStore?.store_number;
        const storeState = selectedStore?.address?.administrative_area?.toUpperCase();
        if (storeNumber && storeState) {
          // Fire-and-forget: getStateFromPath is synchronous in React Nav v7.
          // `void` + `.catch` keeps a transient network error from surfacing
          // as an unhandled promise rejection on Hermes.
          void queryClient
            .prefetchInfiniteQuery({
              queryKey: feedDiscoverListQueryKey(storeNumber, storeState, ALL_SEGMENT_VALUE),
              queryFn: ({pageParam}) =>
                feedDiscoverListQueryFn(storeNumber, storeState, ALL_SEGMENT_VALUE, pageParam),
              initialPageParam: 0,
            })
            .catch(err => logger.warn('AppNavigationContainer', 'discover prefetch failed', {err}));
        }
      }
      return originalNavigationState;
    },
    getInitialURL: async () => {
      logger.debug('OneSignal', 'getInitialURL');

      const lastNotificationUrl = getLastNotificationUrl();
      if (lastNotificationUrl) {
        logger.debug('OneSignal', 'Returning last notification URL:', {lastNotificationUrl});
        clearLastNotificationUrl();
        return lastNotificationUrl;
      }

      return await Linking.getInitialURL();
    },
  };

  const routeNameRef = useRef<string | undefined>();

  const handleNavigationContainerReady = () => {
    routeNameRef.current = getCurrentRouteName();
    globalStore.setIsHomeRoute(getCurrentRouteName() === AppRoute.HOME);
  };

  const handleNavigationContainerStateChange = () => {
    const currentRouteName = getCurrentRouteName() || '';
    const previousRouteName = routeNameRef.current;
    globalStore.setIsHomeRoute(currentRouteName === AppRoute.HOME);
    if (previousRouteName !== currentRouteName) {
      globalStore.setIsAccountRoute(false);
      globalStore.setIsInformationRoute(false);
      logger.debug('AppNavigationContainer', 'Navigating to route', {currentRouteName});

      // Track screen for Crashlytics context
      crashlyticsService.setCurrentScreen(currentRouteName);
    }
    routeNameRef.current = currentRouteName;
  };

  // Removed handleUnhandledAction to prevent infinite loop
  // React Navigation v7 handles unhandled actions properly by default

  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      onReady={handleNavigationContainerReady}
      onStateChange={_state => {
        handleNavigationContainerStateChange();
        // TODO uncomment this when we fix restoreState() above
        // if (Platform.OS === 'web') {
        //   sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(state));
        //   sessionStorage.setItem(
        //     NAVIGATION_TIMESTAMP_KEY,
        //     Date.now().toString(),
        //   );
        //   // Slight hack, to ensure window.location.href has updated
        //   setTimeout(() => {
        //     sessionStorage.setItem(NAVIGATION_URL_KEY, window.location.href);
        //   }, 0);
        // }
      }}
      initialState={initialNavigationState}
      linking={linking}
      ref={RootNavigation.navigationRef}
      theme={theme}
      documentTitle={{
        formatter: () => config.APP_NAME,
      }}>
      {children}
    </NavigationContainer>
  );
}
